import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useMemo, useState, type PropsWithChildren } from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useMediaQuery from 'shared/hooks/use-media-query';
import { useViewportTop } from 'shared/hooks/use-viewport-top';
import { CONTENT_FRAME_CLASSNAME, CONTENT_HORIZONTAL_PADDING_CLASSNAME } from 'shared/layout/constants/layout-class-names';
import { twJoin, twMerge } from 'tailwind-merge';
interface FilterSidebar {
  /** Null until the column mounts; the page already has one, so placement need not wait for it. */
  element: HTMLElement | null;
}
const FilterSidebarContext = createContext<FilterSidebar | null>(null);

/** The column a complex filter group docks its panel into on desktop, when the page has one. */
export const useFilterSidebar = () => {
  return useContext(FilterSidebarContext);
};

/**
 * A page with a permanent filter column on its left edge. The column stays empty until the page's
 * `FilterHeader` docks its complex group there; below desktop it is not shown and the group keeps
 * its drawer. The column runs from the edge of the page frame rather than inside the content
 * padding, so the page renders in `DashboardLayoutShell` and this layout draws the content beside it.
 */
export function FilterSidebarLayout(t0) {
  const $ = _c(17);
  const {
    children
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const [element, setElement] = useState(null);
  const {
    isDesktop
  } = useMediaQuery();
  const top = useViewportTop(isDesktop ? element : null);
  let t1;
  if ($[0] !== element) {
    t1 = {
      element
    };
    $[0] = element;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sidebar = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = twJoin(CONTENT_FRAME_CLASSNAME, "flex");
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = t("global.filter");
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== top) {
    t4 = top === undefined ? undefined : {
      height: `calc(100dvh - ${top}px)`
    };
    $[5] = top;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <aside ref={setElement} aria-label={t3} style={t4} className="desktop:flex border-border-card bg-bg-primary sticky top-(--sticky-top) hidden h-[calc(100dvh-var(--sticky-top))] w-90 shrink-0 flex-col border-x pt-5" />;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = twMerge(CONTENT_HORIZONTAL_PADDING_CLASSNAME, "desktop:pl-6 min-w-0 flex-1 py-5");
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== children || $[12] !== sidebar) {
    t7 = <main className={t6}><FilterSidebarContext.Provider value={sidebar}>{children}</FilterSidebarContext.Provider></main>;
    $[11] = children;
    $[12] = sidebar;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t5 || $[15] !== t7) {
    t8 = <div className={t2}>{t5}{t7}</div>;
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}